import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

// relative imports
import { useAccountConfig } from '../providers/AccountConfigProvider';
import { useAccount } from '../providers/AccountProvider';
import LedgibleFab from '../shared/custom-components/buttons/LedgibleFab';
import { openZendeskWidget } from '../shared/customer-support/zendesk-operations';

const AccountConfigError = ({ onRetry }) => {
  const { configError, clearConfigError, updateAccountConfig } =
    useAccountConfig();
  const { currentAccount } = useAccount();

  const handleRetry = async () => {
    clearConfigError();
    if (onRetry) {
      onRetry();
    } else {
      await updateAccountConfig();
    }
  };

  if (!configError) return null;

  return (
    <Box
      data-testid="account-config-error"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'start',
        paddingTop: 8,
        height: '100%',
        backgroundColor: 'primary.main',
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Account Configuration Error</AlertTitle>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Unable to load configuration for account application type:{' '}
              <strong>{configError.applicationTypeId}</strong>
            </Typography>
          </Alert>

          <Typography variant="subtitle1" gutterBottom>
            What happened?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The system could not find a valid configuration file for your
            account&apos;s application type. This usually happens when:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 3 }}>
            <li>The account application type ID is invalid or has been removed</li>
            <li>The configuration file is temporarily unavailable</li>
            <li>There&apos;s a network connectivity issue</li>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            Next Steps
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <LedgibleFab
              variant="extended"
              onClick={handleRetry}
              color="primaryDark"
              size="small"
              id="account-config-error-try-again-button"
            >
              <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
              Try Again
            </LedgibleFab>
            <LedgibleFab
              variant="extended"
              color="secondary"
              size="small"
              onClick={() => openZendeskWidget()}
              id="add-source-modal-no-source-found-contact-support-button"
            >
              Contact Support
            </LedgibleFab>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Debug Info:</strong>
                <br />
                Error: {configError.message}
                <br />
                Application Type ID: {configError.applicationTypeId}
                <br />
                Account: {currentAccount.name} (ID: {currentAccount.id})<br />
                Time: {new Date(configError.timestamp).toLocaleString()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

AccountConfigError.propTypes = {
  onRetry: PropTypes.func,
};

AccountConfigError.defaultProps = {
  onRetry: null,
};

export default AccountConfigError;
